import { createRoot, createSignal } from 'solid-js';
import { geolocation } from '~/lib/geolocation';

/** A coordinate pair in WGS84 degrees. */
export type LatLng = [number, number];

/** ~1.65 km rectangular loop through the downtown Trinidad cluster, CO.
 *  Edit this constant to retune the route. Chosen so heat's distance-decay is
 *  testable at the desk: the START corner sits within engagement range (≤200 m)
 *  of several real target nodes (GLACIER QUARTER ~44 m, PALE VAULT, EMBER
 *  MINISTRY, ...), so a tester can fail a hack there to create a heat origin,
 *  then the loop carries the player up to ~590 m away (decay-rate ×2.5 at the
 *  far corner vs ×1.0 at the origin) and back. At 1.4 m/s the full loop is
 *  ~20 min; crank the DIAG speed slider for faster traversal (log timestamps
 *  stay real wall-clock, so decay rates read true regardless of sim speed).
 *
 *  Economy-loop coverage (prompt 27): using the committed GeoJSON coordinates,
 *  the loop passes within engagement range of all four roles, so the full
 *  earn→spend loop and both dual outcomes are exercisable in one traversal:
 *  a target to earn ¤ (~12 m), a pure service for heat reduction (~13 m), a
 *  pure shop to buy a tool (~154 m), and dual nodes for the success surcharge
 *  and the failure lockout (a social dual ~44 m, a retail dual ~69 m). The east
 *  edge was nudged out from −104.501 to −104.4997 (≈ +115 m) to bring the pure
 *  shop at [37.1716, −104.4980] inside 200 m; the loop shape is unchanged. */
export const TRINIDAD_LOOP: LatLng[] = [
  [37.1685, -104.505], // START — beside the GLACIER QUARTER / EMBER MINISTRY cluster
  [37.1685, -104.4997], // east ~470 m (passes the pure shop near the NE corner)
  [37.1717, -104.4997], // north ~356 m (farthest from start, ~590 m)
  [37.1717, -104.505], // west ~470 m
];

const EARTH_RADIUS_M = 6_371_000;

/** Haversine distance between two coordinates in meters. */
function haversine(a: LatLng, b: LatLng): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(x));
}

/** Linear interpolation between two coordinates. */
function lerp(a: LatLng, b: LatLng, t: number): LatLng {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/** Synthetic position generator that walks the route at a configurable speed,
 *  emitting simulated fixes into the geolocation singleton once per second.
 *
 *  Module-level singleton (createRoot), like geolocation/wake-lock/visibility/
 *  nodes: its run loop lives at module scope, so it KEEPS RUNNING across the
 *  dock's `<Show>` tab switches (DIAG is a thin control surface, not the
 *  owner). While active it claims `geolocation.setSimOverride(true)`, so the
 *  real device watcher's fixes are dropped and the simulated position is the
 *  sole source app-wide (MAP, heat decay, distance, engagement). Stopping
 *  releases the override and the real watcher resumes.
 *
 *  Dev-only by convention: the DIAG controls that call `start()` are gated on
 *  `import.meta.env.DEV`; the singleton is inert until started. */
function createGhostWalk(polyline: LatLng[]) {
  const [active, setActive] = createSignal(false);
  const [speedMps, setSpeedMps] = createSignal(1.4);
  const [segmentIndex, setSegmentIndex] = createSignal(0);
  const [position, setPosition] = createSignal<LatLng>(polyline[0] ?? [0, 0]);

  let progress = 0;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  const tickMs = 1000;

  const advance = () => {
    if (polyline.length < 2) return;
    // speed 0 → stepM 0 → the while-loop below is skipped, so position is held
    // in place while the tick still re-emits the (frozen) fix. This keeps the
    // sim the authoritative position source AND gives heat a true zero-distance
    // standing-still measurement. Raising speed resumes from the held point.
    const stepM = speedMps() * (tickMs / 1000);
    let remaining = stepM;
    let idx = segmentIndex();
    let pos = position();
    let p = progress;

    while (remaining > 0) {
      const from = polyline[idx];
      const to = polyline[(idx + 1) % polyline.length];
      if (!from || !to) break;
      const segLen = haversine(from, to);
      if (segLen === 0) {
        idx = (idx + 1) % polyline.length;
        p = 0;
        continue;
      }
      const segRemainingM = (1 - p) * segLen;
      if (remaining < segRemainingM) {
        p += remaining / segLen;
        pos = lerp(from, to, p);
        remaining = 0;
      } else {
        remaining -= segRemainingM;
        idx = (idx + 1) % polyline.length;
        p = 0;
        pos = polyline[idx] ?? pos;
      }
    }

    progress = p;
    setSegmentIndex(idx);
    setPosition(pos);
    geolocation.pushSimulatedFix(pos[0], pos[1], speedMps());
  };

  const start = () => {
    if (intervalId !== null) return; // guard double-start (no stacked loops)
    setActive(true);
    geolocation.setSimOverride(true); // sim owns position app-wide while active
    advance(); // emit immediately so MAP/heat snap to the route at once
    intervalId = setInterval(advance, tickMs);
  };

  const stop = () => {
    if (intervalId !== null) clearInterval(intervalId);
    intervalId = null;
    setActive(false);
    geolocation.setSimOverride(false); // real watcher resumes ownership
  };

  // No onCleanup: this singleton's loop intentionally outlives any screen.

  return { active, speedMps, setSpeedMps, position, segmentIndex, start, stop };
}

/** App-wide ghost-walk singleton. Survives tab switches; the DIAG controls
 *  drive it. */
export const ghostWalk = createRoot(() => createGhostWalk(TRINIDAD_LOOP));
